import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, LinearProgress, MenuItem,
  Pagination, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import CodeIcon from '@mui/icons-material/Code';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  downloadBlob,
  downloadFiscalDocumentsXmlZip,
  downloadNfceVendaPdf,
  downloadNfceVendaXml,
  downloadNfeGerencialPdf,
  downloadNfeGerencialXml,
  getConfiguracao,
  getEstabelecimentos,
  getFiscalDocumentsReport,
  sendFiscalDocumentsContador,
  sendFiscalDocumentsEmail,
  sendFiscalDocumentsWhatsApp,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import PageHeader from '../common/PageHeader';
import SortableTh from '../common/SortableTh';
import { useClientSort } from '../../hooks/useClientSort';
import ResponsiveTable from '../common/ResponsiveTable';
import { toDateInputValue } from '../../utils/datetimeLocal';
import { useEstabelecimento } from '../../context/EstabelecimentoContext';

const money = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const localDate = toDateInputValue;
const docKey = (item) => `${item.tipo}-${item.id}`;

const mesAtualInput = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};

export default function FiscalDocumentsReport() {
  const { estabelecimentoId: filialNavbar, estabelecimentos } = useEstabelecimento();
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const [filters, setFilters] = useState({
    de: localDate(first),
    ate: localDate(now),
    tipo: 'todos',
    status: '',
    estabelecimentoId: '',
  });
  const [estabs, setEstabs] = useState([]);
  const [data, setData] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailDlg, setEmailDlg] = useState({ open: false, email: '', incluirPdf: true, xmlZipado: true });
  const [waDlg, setWaDlg] = useState({ open: false, telefone: '', incluirXml: true });
  const [contadorDlg, setContadorDlg] = useState({
    open: false,
    mes: mesAtualInput(),
    email: '',
    telefone: '',
    enviarEmail: true,
    enviarWhatsApp: true,
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (estabelecimentos.length > 0) {
      setEstabs(estabelecimentos.map((e) => ({
        id: e.id,
        nome: e.nomeFantasia || e.razaoSocial || `Estab. ${e.id}`,
      })));
      return;
    }
    (async () => {
      try {
        const { data: lista } = await getEstabelecimentos();
        const arr = Array.isArray(lista) ? lista : (lista ? [lista] : []);
        setEstabs(arr.map((e) => ({
          id: e.id,
          nome: e.nomeFantasia || e.razaoSocial || `Estab. ${e.id}`,
        })));
      } catch {
        setEstabs([]);
      }
    })();
  }, [estabelecimentos]);

  useEffect(() => {
    if (!filialNavbar) return;
    setFilters((prev) => ({ ...prev, estabelecimentoId: String(filialNavbar) }));
    setPagina(1);
  }, [filialNavbar]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        de: filters.de,
        ate: filters.ate,
        tipo: filters.tipo,
        pagina,
        qtde: 50,
      };
      if (filters.status) params.status = filters.status;
      if (filters.estabelecimentoId) params.estabelecimentoId = filters.estabelecimentoId;
      const { data: resp } = await getFiscalDocumentsReport(params);
      setData(resp);
      setSelected({});
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível carregar os documentos fiscais.'));
    } finally {
      setLoading(false);
    }
  }, [filters, pagina]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const fiscalAccessors = useMemo(() => ({
    numero: (i) => `${i.numero}/${i.serie}`,
    data: (i) => i.dataEmissao,
    cliente: (i) => i.clienteNome || i.documento || '',
    valor: (i) => Number(i.valorTotal) || 0,
  }), []);
  const { sorted: itemsOrdenados, orderBy, order, requestSort } = useClientSort(
    items,
    'dataEmissao',
    'desc',
    fiscalAccessors,
  );
  const selectedItems = useMemo(
    () => items.filter((i) => selected[docKey(i)]),
    [items, selected],
  );
  const selectedRefs = useMemo(
    () => selectedItems.map((i) => ({ tipo: i.tipo, id: i.id })),
    [selectedItems],
  );

  const toggleAll = (checked) => {
    if (!checked) {
      setSelected({});
      return;
    }
    const next = {};
    items.forEach((i) => { next[docKey(i)] = true; });
    setSelected(next);
  };

  const baixarPdf = async (item) => {
    try {
      const { data: blob } = item.tipo === 'nfe'
        ? await downloadNfeGerencialPdf(item.id)
        : await downloadNfceVendaPdf(item.id);
      downloadBlob(blob, item.tipo === 'nfe' ? `nfe-${item.numero}.pdf` : `nfce-${item.numero}.pdf`);
    } catch (e) {
      setError(getApiErrorMessage(e, 'PDF não disponível.'));
    }
  };

  const baixarXml = async (item) => {
    try {
      const { data: blob } = item.tipo === 'nfe'
        ? await downloadNfeGerencialXml(item.id)
        : await downloadNfceVendaXml(item.id);
      const nome = item.chave ? `NFe${item.chave}.xml` : `${item.tipo}-${item.numero}.xml`;
      downloadBlob(blob, nome);
    } catch (e) {
      setError(getApiErrorMessage(e, 'XML não disponível.'));
    }
  };

  const baixarZip = async () => {
    try {
      const params = {
        de: filters.de,
        ate: filters.ate,
        tipo: filters.tipo,
      };
      if (filters.status) params.status = filters.status;
      if (filters.estabelecimentoId) params.estabelecimentoId = filters.estabelecimentoId;
      const { data: blob } = await downloadFiscalDocumentsXmlZip(params);
      downloadBlob(blob, `xmls-fiscais-${filters.de}-${filters.ate}.zip`);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível gerar o ZIP de XMLs.'));
    }
  };

  const abrirContador = async () => {
    const mesFiltro = (filters.de || '').slice(0, 7) || mesAtualInput();
    let email = '';
    const estabId = Number(filters.estabelecimentoId || filialNavbar || 0);
    if (estabId > 0) {
      try {
        const { data: cfg } = await getConfiguracao(estabId);
        email = (cfg?.emailContador || '').trim();
      } catch {
        /* ignora — usuário digita */
      }
    }
    setContadorDlg({
      open: true,
      mes: mesFiltro,
      email,
      telefone: '',
      enviarEmail: true,
      enviarWhatsApp: true,
    });
  };

  const enviarContador = async () => {
    const [anoStr, mesStr] = (contadorDlg.mes || '').split('-');
    const ano = Number(anoStr);
    const mes = Number(mesStr);
    if (!ano || !mes) {
      setError('Informe o mês de referência.');
      return;
    }
    if (!contadorDlg.enviarEmail && !contadorDlg.enviarWhatsApp) {
      setError('Marque e-mail e/ou WhatsApp.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const { data: resp } = await sendFiscalDocumentsContador({
        mes,
        ano,
        email: contadorDlg.email.trim() || undefined,
        telefone: contadorDlg.telefone.trim() || undefined,
        enviarEmail: contadorDlg.enviarEmail,
        enviarWhatsApp: contadorDlg.enviarWhatsApp,
        tipo: filters.tipo,
        status: filters.status || undefined,
        estabelecimentoId: filters.estabelecimentoId
          ? Number(filters.estabelecimentoId)
          : undefined,
      });
      const canais = [
        resp?.email ? `e-mail (${resp.email})` : null,
        resp?.whatsapp ? `WhatsApp (${resp.whatsapp})` : null,
      ].filter(Boolean).join(' e ');
      setSuccess(`ZIP de ${resp?.mes || contadorDlg.mes} enviado por ${canais || 'canal(is) selecionado(s)'}.`);
      setContadorDlg((d) => ({ ...d, open: false }));
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao enviar ZIP ao contador.'));
    } finally {
      setSending(false);
    }
  };

  const enviarEmail = async () => {
    setSending(true);
    setError('');
    try {
      const payload = {
        email: emailDlg.email.trim(),
        de: filters.de,
        ate: filters.ate,
        tipo: filters.tipo,
        status: filters.status || undefined,
        estabelecimentoId: filters.estabelecimentoId ? Number(filters.estabelecimentoId) : undefined,
        incluirPdf: emailDlg.incluirPdf,
        xmlZipado: emailDlg.xmlZipado,
        documentos: selectedRefs.length > 0 ? selectedRefs : undefined,
      };
      await sendFiscalDocumentsEmail(payload);
      setSuccess('E-mail enviado com sucesso.');
      setEmailDlg({ open: false, email: '', incluirPdf: true, xmlZipado: true });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao enviar e-mail.'));
    } finally {
      setSending(false);
    }
  };

  const enviarWhatsApp = async () => {
    if (selectedRefs.length === 0) {
      setError('Selecione ao menos um documento para enviar por WhatsApp.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await sendFiscalDocumentsWhatsApp({
        telefone: waDlg.telefone,
        incluirXml: waDlg.incluirXml,
        documentos: selectedRefs,
      });
      setSuccess('Documento(s) enviado(s) por WhatsApp.');
      setWaDlg({ open: false, telefone: '', incluirXml: true });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao enviar WhatsApp.'));
    } finally {
      setSending(false);
    }
  };

  const statusColor = (s) => {
    if (s === 'autorizada') return 'success';
    if (s === 'cancelada') return 'error';
    return 'default';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Documentos fiscais emitidos"
        subtitle="NF-e e NFC-e autorizadas ou canceladas — download, ZIP, e-mail e WhatsApp."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <TextField label="De" type="date" size="small" value={filters.de}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, de: e.target.value })); }} InputLabelProps={{ shrink: true }} />
          <TextField label="Até" type="date" size="small" value={filters.ate}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, ate: e.target.value })); }} InputLabelProps={{ shrink: true }} />
          <TextField select label="Tipo" size="small" value={filters.tipo} sx={{ minWidth: 140 }}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, tipo: e.target.value })); }}>
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="nfe">NF-e</MenuItem>
            <MenuItem value="nfce">NFC-e</MenuItem>
          </TextField>
          <TextField select label="Status" size="small" value={filters.status} sx={{ minWidth: 140 }}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, status: e.target.value })); }}>
            <MenuItem value="">Todos emitidos</MenuItem>
            <MenuItem value="autorizada">Autorizada</MenuItem>
            <MenuItem value="cancelada">Cancelada</MenuItem>
          </TextField>
          <TextField select label="Estabelecimento" size="small" value={filters.estabelecimentoId} sx={{ minWidth: 200 }}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, estabelecimentoId: e.target.value })); }}>
            <MenuItem value="">Todos</MenuItem>
            {estabs.map((e) => <MenuItem key={e.id} value={String(e.id)}>{e.nome}</MenuItem>)}
          </TextField>
          <Button variant="outlined" startIcon={<ArchiveIcon />} onClick={baixarZip}>XMLs (ZIP)</Button>
          <Button variant="contained" color="secondary" startIcon={<SendOutlinedIcon />} onClick={abrirContador}>
            Enviar ao contador
          </Button>
          <Button variant="outlined" startIcon={<EmailOutlinedIcon />} onClick={() => setEmailDlg({ open: true, email: '', incluirPdf: true, xmlZipado: true })}>
            Enviar e-mail
          </Button>
          <Button variant="outlined" startIcon={<WhatsAppIcon />} disabled={selectedRefs.length === 0}
            onClick={() => setWaDlg({ open: true, telefone: '', incluirXml: true })}>
            WhatsApp ({selectedRefs.length})
          </Button>
        </Stack>
        {data && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {data.totalItems} documento(s) · Total {money(data.valorTotal)}
          </Typography>
        )}
      </Paper>

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedRefs.length > 0 && selectedRefs.length < items.length}
                  checked={items.length > 0 && selectedRefs.length === items.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </TableCell>
              <SortableTh id="tipo" label="Tipo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="numero" label="Número" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="data" label="Data" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cliente" label="Cliente" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="statusFiscal" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="valor" label="Valor" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>Nenhum documento no período</TableCell>
              </TableRow>
            ) : itemsOrdenados.map((item) => (
              <TableRow key={docKey(item)} hover selected={!!selected[docKey(item)]}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={!!selected[docKey(item)]}
                    onChange={(e) => setSelected((s) => ({ ...s, [docKey(item)]: e.target.checked }))}
                  />
                </TableCell>
                <TableCell data-label="Tipo">
                  <Chip size="small" label={item.tipo === 'nfe' ? 'NF-e' : 'NFC-e'} color={item.tipo === 'nfe' ? 'primary' : 'secondary'} />
                </TableCell>
                <TableCell data-label="Número">{item.numero}/{item.serie}</TableCell>
                <TableCell data-label="Data">{new Date(item.dataEmissao).toLocaleString('pt-BR')}</TableCell>
                <TableCell data-label="Cliente">{item.clienteNome || item.documento || '—'}</TableCell>
                <TableCell data-label="Status">
                  <Chip size="small" color={statusColor(item.statusFiscal)} label={item.statusFiscal} />
                </TableCell>
                <TableCell align="right" data-label="Valor">{money(item.valorTotal)}</TableCell>
                <TableCell align="right" data-actions="">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {item.temPdf && (
                      <Tooltip title="PDF">
                        <IconButton size="small" onClick={() => baixarPdf(item)}><PictureAsPdfIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {item.temXml && (
                      <Tooltip title="XML">
                        <IconButton size="small" onClick={() => baixarXml(item)}><CodeIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack alignItems="center" sx={{ mt: 2 }}>
        <Pagination
          count={data?.totalPages || 1}
          page={pagina}
          onChange={(_e, p) => setPagina(p)}
          color="primary"
        />
      </Stack>

      <Dialog open={emailDlg.open} onClose={() => setEmailDlg({ open: false, email: '', incluirPdf: true, xmlZipado: true })} fullWidth maxWidth="sm">
        <DialogTitle>Enviar documentos por e-mail</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField label="E-mail" type="email" fullWidth value={emailDlg.email}
              onChange={(e) => setEmailDlg((d) => ({ ...d, email: e.target.value }))} autoFocus />
            <FormControlLabel control={<Checkbox checked={emailDlg.xmlZipado}
              onChange={(e) => setEmailDlg((d) => ({ ...d, xmlZipado: e.target.checked }))} />}
              label="XMLs zipados por período" />
            <FormControlLabel control={<Checkbox checked={emailDlg.incluirPdf}
              onChange={(e) => setEmailDlg((d) => ({ ...d, incluirPdf: e.target.checked }))} />}
              label="Incluir PDFs (DANFE)" />
            {selectedRefs.length > 0 && (
              <Alert severity="info">Enviando {selectedRefs.length} documento(s) selecionado(s).</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDlg({ open: false, email: '', incluirPdf: true, xmlZipado: true })}>Cancelar</Button>
          <Button variant="contained" disabled={sending} onClick={enviarEmail}>
            {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={waDlg.open} onClose={() => setWaDlg({ open: false, telefone: '', incluirXml: true })} fullWidth maxWidth="sm">
        <DialogTitle>Enviar por WhatsApp</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField label="WhatsApp (com DDD)" fullWidth value={waDlg.telefone}
              onChange={(e) => setWaDlg((d) => ({ ...d, telefone: e.target.value }))} autoFocus />
            <FormControlLabel control={<Checkbox checked={waDlg.incluirXml}
              onChange={(e) => setWaDlg((d) => ({ ...d, incluirXml: e.target.checked }))} />}
              label="Incluir XML após o PDF" />
            <Alert severity="info">{selectedRefs.length} documento(s) selecionado(s).</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaDlg({ open: false, telefone: '', incluirXml: true })}>Cancelar</Button>
          <Button variant="contained" disabled={sending} onClick={enviarWhatsApp}>
            {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contadorDlg.open}
        onClose={() => setContadorDlg((d) => ({ ...d, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Enviar ZIP do mês ao contador</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              Gera um único ZIP com todos os XMLs do mês (conforme tipo/status/filial dos filtros)
              e envia o mesmo arquivo por e-mail e/ou WhatsApp.
            </Alert>
            <TextField
              label="Mês de referência"
              type="month"
              fullWidth
              value={contadorDlg.mes}
              onChange={(e) => setContadorDlg((d) => ({ ...d, mes: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={contadorDlg.enviarEmail}
                  onChange={(e) => setContadorDlg((d) => ({ ...d, enviarEmail: e.target.checked }))}
                />
              )}
              label="Enviar por e-mail"
            />
            {contadorDlg.enviarEmail && (
              <TextField
                label="E-mail do contador"
                type="email"
                fullWidth
                value={contadorDlg.email}
                onChange={(e) => setContadorDlg((d) => ({ ...d, email: e.target.value }))}
                helperText="Pré-preenchido com o e-mail do contador da configuração fiscal, se houver."
              />
            )}
            <FormControlLabel
              control={(
                <Checkbox
                  checked={contadorDlg.enviarWhatsApp}
                  onChange={(e) => setContadorDlg((d) => ({ ...d, enviarWhatsApp: e.target.checked }))}
                />
              )}
              label="Enviar por WhatsApp"
            />
            {contadorDlg.enviarWhatsApp && (
              <TextField
                label="WhatsApp do contador (com DDD)"
                fullWidth
                value={contadorDlg.telefone}
                onChange={(e) => setContadorDlg((d) => ({ ...d, telefone: e.target.value }))}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContadorDlg((d) => ({ ...d, open: false }))}>Cancelar</Button>
          <Button variant="contained" disabled={sending} onClick={enviarContador}>
            {sending ? 'Gerando e enviando...' : 'Gerar ZIP e enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
